import React, { ReactNode } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { GenNode } from '../types/carbon/models/GenNode'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import PersonIcon from '@mui/icons-material/Person'
import DescriptionIcon from '@mui/icons-material/Description'
import TableChartIcon from '@mui/icons-material/TableChart'
import ListIcon from '@mui/icons-material/List'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import PendingIcon from '@mui/icons-material/Pending'
import PeopleIcon from '@mui/icons-material/People'

export interface NodeTypeDefinition {
  hasChildren: boolean;
  getTitle: (node: GenNode) => string;
  getIcon: (node: GenNode, expanded?: boolean) => ReactNode;
  getID?: (node: GenNode) => string;
  getColor?: (node: GenNode) => string;
  getClassName?: (node: GenNode) => string;
}

const getSimpleTitle = (node: GenNode) => node.value1 || ''
const getNetTitle = (node: GenNode) => `${node.value2} : ${node.value1}`
const getSimpleID = (node: GenNode) => node.id?.toString() || ''
const getPathID = (node: GenNode) => {
  // Normalize backslashes to forward slashes for consistent URL paths
  return `${node.value2}/${node.value1}`.replace(/\\/g, '/')
}
const getSimpleIDWithParent = (node: GenNode) => `${node.parentId?.toString()}-${node.id?.toString()}` || ''

export const NODE_TYPE_DEFINITIONS: Record<string, NodeTypeDefinition> = {
  'Loading': {
    hasChildren: false,
    getTitle: (node) => '',
    getIcon: (_) => <CircularProgress size={24} sx={{ color: "#00B2FF", verticalAlign: 'middle' }} />,
    getColor: () => '#00B2FF',
  },
  'Section': {
    hasChildren: true,
    getTitle: getSimpleTitle,
    getIcon: (_, expanded) => expanded ? <FolderOpenIcon /> : <FolderIcon />,
    getColor: () => '#00B2FF',
  },
  'Folder': {
    hasChildren: true,
    getTitle: getSimpleTitle,
    getID: getPathID,
    getIcon: (_, expanded) => expanded ? <FolderOpenIcon /> : <FolderIcon />,
    getColor: () => '#FFC000',
  },
  'User': {
    hasChildren: true,
    getTitle: getSimpleTitle,
    getIcon: () => <PersonIcon />,
    getColor: () => '#4caf50',
  },
  'Colleague': {
    hasChildren: true,
    getTitle: getSimpleTitle,
    getIcon: () => <PeopleIcon />,
    getColor: () => '#9c27b0',
  },
  'Table': {
    hasChildren: true,
    getTitle: (node) => {
      const name = getSimpleTitle(node).replace(/\.cbt$/, '')
      const lastSlash = name.lastIndexOf('/')
      return lastSlash >= 0 ? name.substring(lastSlash + 1) : name
    },
    getIcon: () => <TableChartIcon />,
    getColor: () => '#00B2FF',
  },
  'Variable': {
    hasChildren: true,
    getTitle: (node) => {
      const question = node.meta?.find(item => item.key === 'question')?.value
      return question ? `${node.value1} - ${question}` : `${node.value1}`
    },
    getID: getPathID,
    getIcon: () => <ExpandCircleDownIcon />,
    getColor: () => '#cc0000',
  },
  'MultiTable': {
    hasChildren: false,
    getTitle: getSimpleTitle,
    getID: (node) => {
      const metaId = node.meta?.find(item => item.key === 'multiTableId')?.value
      return metaId ? `multiTable/${metaId}` : getPathID(node)
    },
    getIcon: () => <ListIcon />,
    getColor: () => '#1976d2',
  },
  'Filter': {
    hasChildren: true,
    getTitle: getSimpleTitle,
    getID: getSimpleID,
    getIcon: () => <ExpandCircleDownIcon />,
    getColor: () => '#cc0000',
  },
  'Net': {
    hasChildren: false,
    getTitle: getNetTitle,
    getID: getSimpleID,
    getIcon: () => <PendingIcon />,
    getColor: () => '#4caf50',
  },
  'Codeframe': {
    hasChildren: true,
    // A frame the server built from bare syntax (`category()`) has no label
    getTitle: (node) => node.value2 == null ? `${node.value1}` : `${node.value1} - ${node.value2}`,
    getID: getSimpleID,
    getIcon: () => <ExpandCircleDownIcon />,
    getColor: () => '#4caf50',
  },
  'Code': {
    hasChildren: false,
    getTitle: (node) => `${node.value1} - ${node.value2}`,
    getID: getSimpleID,
    getIcon: () => <PendingIcon />,
    getColor: () => '#4caf50',
  },
  'Base': {
    hasChildren: false,
    getTitle: (node) => `${node.value1} - ${node.value2}`,
    getID: getSimpleID,
    getIcon: () => <PendingIcon />,
    getColor: () => '#4caf50',
  },
  'Stat': {
    hasChildren: false,
    getTitle: (node) => `${node.value1} - ${node.value2}`,
    getID: getSimpleID,
    getIcon: () => <PendingIcon />,
    getColor: () => '#4caf50',
  },
  'default': {
    hasChildren: false,
    getTitle: getSimpleTitle,
    getIcon: () => <DescriptionIcon />,
    getColor: () => '#757575',
  }
}

// Map node types to their corresponding icon names
const iconMap: { [key: string]: string } = {
  'Table': 'NodeTable',
  'User': 'NodeUser',
  'Folder': 'NodeFolder',
  'Function': 'NodeFunction',
  'Base': 'NodeBase',
  'Code': 'NodeCode',
  'Filter': 'NodeFilter',
  'Variable': 'NodeVariable',
  'Weight': 'NodeWeight',
  'Net': 'NodeNet',
  'Stat': 'NodeStat',
  'Nes': 'NodeNes',
  'Axis': 'NodeAxis',
  'TabAxis': 'NodeTabAxis',
  'Arith': 'NodeArith',
  'Codeframe': 'NodeCodeframe'
}

export const OldNodeIcon: React.FC<{
  node: GenNode
}> = ({ node }) => {
  if (node.type === 'MultiTable') {
    return (
      <ListIcon
        sx={{ 
          width: 20,
          height: 20,
          mr: 1,
          color: 'primary.main'
        }}
      />
    )
  }

  const hasPC = node.meta?.some(m => m.key === 'PC')
  const baseIconName = iconMap[node.type || ''] || 'NodeFolder'
  const iconName = hasPC ? `${baseIconName}PC` : baseIconName

  return (
    <Box 
      component="img"
      src={`/icons/${iconName}.png`}
      alt={node.type ?? ''}
      sx={{ 
        width: 20,
        height: 20,
        mr: 1,
        objectFit: 'contain'
      }}
    />
  )
}


export const getNodeDefinition = (node: GenNode) => NODE_TYPE_DEFINITIONS[node.type || 'default'] || NODE_TYPE_DEFINITIONS['default']
export const canNodeHaveChildren = (node: GenNode) => {
  const definition = getNodeDefinition(node)
  if (node.type && definition) {
    return definition.hasChildren
  }
  // For unknown types, check if the node actually has children
  return (node.children && node.children.length > 0) || false
}
export const getNodeTitle = (node: GenNode) => {
  const definition = getNodeDefinition(node)
  return definition.getTitle(node) || ''
}
export const getNodeID = (node: GenNode) => {
  const definition = getNodeDefinition(node)
  // Normalize backslashes to forward slashes for consistent URL paths
  const id = definition.getID?.(node) || `${node.value2}/${node.value1}`
  return id.replace(/\\/g, '/')
}

const USE_NEW_ICONS = ['section', 'folder', 'table', 'loading', 'user', 'multitable', 'colleague']
export const getNodeIcon = (node: GenNode, expanded = false) => {
  const definition = getNodeDefinition(node)
  return USE_NEW_ICONS.includes(node.type?.toLowerCase() || '') ? definition.getIcon(node, expanded) : <OldNodeIcon node={node} />
}
export const getNodeClassName = (node: GenNode) => {
  const definition = getNodeDefinition(node)
  return definition.getClassName?.(node) || ''
}
export const getNodeColor = (node: GenNode) => {
  const definition = getNodeDefinition(node)
  return definition.getColor?.(node) || '#757575'
}

// export interface NodeTypeDefinition {
//   type: string;
//   allowedLevels: number[];
//   canHaveChildren: boolean;
//   validParents: string[];
//   requiredFields: string[];
//   optionalFields: string[];
//   metaOptions: {
//     key: string;
//     value: string;
//     description: string;
//   }[];
// }

// export const VALID_PARENT_CHILDREN: Record<string, string[]> = {
//   'TabAxis': ['Codeframe', 'Filter', 'Function', 'Spacer'],
//   'Codeframe': ['Code', 'Base', 'Stat', 'Function', 'Spacer'],
//   'Filter': ['Codeframe', 'Function'],
// };

// export const NODE_TYPE_DEFINITIONS: Record<string, NodeTypeDefinition> = {
//   'TabAxis': {
//     type: 'TabAxis',
//     allowedLevels: [0],
//     canHaveChildren: true,
//     validParents: [],
//     requiredFields: [],
//     optionalFields: ['value2'],
//     metaOptions: []
//   },
  
//   'Codeframe': {
//     type: 'Codeframe',
//     allowedLevels: [1, 2],
//     canHaveChildren: true,
//     validParents: ['TabAxis', 'Filter'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: [
//       { key: 'Base', value: 'cwf', description: 'Base calculation type' },
//       { key: 'PC', value: '1', description: 'Percentage calculation' }
//     ]
//   },

//   'Code': {
//     type: 'Code',
//     allowedLevels: [2, 3],
//     canHaveChildren: false,
//     validParents: ['Codeframe'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: [
//       { key: 'PC', value: '1', description: 'Percentage calculation' }
//     ]
//   },

//   'Filter': {
//     type: 'Filter',
//     allowedLevels: [1],
//     canHaveChildren: true,
//     validParents: ['TabAxis'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: []
//   },

//   'Function': {
//     type: 'Function',
//     allowedLevels: [1, 2],
//     canHaveChildren: false,
//     validParents: ['TabAxis', 'Filter'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: []
//   },

//   'Spacer': {
//     type: 'Spacer',
//     allowedLevels: [1],
//     canHaveChildren: false,
//     validParents: ['TabAxis'],
//     requiredFields: [],
//     optionalFields: ['value1', 'value2'],
//     metaOptions: []
//   },

//   'Base': {
//     type: 'Base',
//     allowedLevels: [2],
//     canHaveChildren: false,
//     validParents: ['Codeframe'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: [
//       { key: 'Base', value: 'cwf', description: 'Weighted Filtered - Count of weighted respondents' },
//       { key: 'Base', value: 'cuf', description: 'Unweighted Filtered - Count of unweighted respondents' },
//       { key: 'Base', value: 'ebf', description: 'Effective Base Filtered - Effective sample size' }
//     ]
//   },

//   'Stat': {
//     type: 'Stat',
//     allowedLevels: [4],
//     canHaveChildren: false,
//     validParents: ['Codeframe'],
//     requiredFields: ['value1'],
//     optionalFields: ['value2'],
//     metaOptions: []
//   }
// }; 
